import { ThreeEvent } from "@react-three/fiber";
import { useEffect, useRef, useState, type JSX } from "react";
import {
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  Vector3,
} from "three";
import { PREVIEW_FEATURE_NAME } from "../preview-feature-renderer";

type PanoMeasureWallRendererProps = {
  /** The first point on the floor of the wall*/
  floorPoint1: Vector3;

  /** The second point on the floor of the wall*/
  floorPoint2: Vector3;

  /** The height of the wall*/
  height: number;

  /** Optional pointer move event handler */
  onPointerMove?(event: ThreeEvent<PointerEvent>): void;

  /** Optional click event handler */
  onClick?(event: ThreeEvent<MouseEvent>): void;

  /** Optional context menu event handler */
  onContextMenu?(event: ThreeEvent<MouseEvent>): void;
};

/**
 * @returns A semi-transparent wall between 2 floor points with a given height.
 * Created by user for visualizing the wall being measured in a pano without depth.
 */
export function PanoMeasureWallRenderer({
  floorPoint1,
  floorPoint2,
  height,
  ...rest
}: PanoMeasureWallRendererProps): JSX.Element {
  const [wallObj] = useState<Mesh>(() => {
    const buffer = new BufferAttribute(
      new Float32Array([
        floorPoint1.x,
        floorPoint1.y,
        floorPoint1.z,
        floorPoint2.x,
        floorPoint2.y,
        floorPoint2.z,
        floorPoint2.x,
        floorPoint2.y + height,
        floorPoint2.z,
        floorPoint1.x,
        floorPoint1.y + height,
        floorPoint1.z,
      ]),
      3,
    );
    const geometry = new BufferGeometry().setAttribute("position", buffer);
    geometry.setIndex([0, 1, 2, 2, 3, 0]);

    const wall = new Mesh(
      geometry,
      new MeshBasicMaterial({
        color: "white",
        transparent: true,
        opacity: 0.5,
        side: DoubleSide,
      }),
    );
    wall.name = PREVIEW_FEATURE_NAME;
    return wall;
  });

  const wallRef = useRef<Mesh>(null);
  useEffect(() => {
    if (!wallRef.current) return;

    const positions = wallRef.current.geometry.getAttribute("position");
    positions.setXYZ(0, floorPoint1.x, floorPoint1.y, floorPoint1.z);
    positions.setXYZ(1, floorPoint2.x, floorPoint2.y, floorPoint2.z);
    positions.setXYZ(2, floorPoint2.x, floorPoint2.y + height, floorPoint2.z);
    positions.setXYZ(3, floorPoint1.x, floorPoint1.y + height, floorPoint1.z);
    positions.needsUpdate = true;
  }, [
    floorPoint1.x,
    floorPoint1.y,
    floorPoint1.z,
    floorPoint2.x,
    floorPoint2.y,
    floorPoint2.z,
    height,
    wallRef,
  ]);

  return (
    <group {...rest}>
      <primitive object={wallObj} ref={wallRef} />
    </group>
  );
}
